import { connect } from "react-redux";

import * as actions from "../../store/actions/index";
import { withRouter } from "../../shared/utility";
import { canManageTenantSettings } from "../../shared/tenantPermissions";
import { CompanySettingsViewModel } from "../../viewmodels/companySettings/CompanySettingsViewModel";
import CompanySettingsForm from "../../components/forms/company-settings/CompanySettingsForm";
import FormSectionLoadingSkeleton from "../../components/operational/FormSectionLoadingSkeleton";
import AuditActionLink from "../../components/audit/AuditActionLink";
import EmptyModal from "../../components/modals/EmptyModal";

function CompanySettingsForbidden() {
  return (
    <div className="min-h-[60vh] bg-[#F6F8FB] px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-2xl rounded-[18px] border border-[#E2E8F0] bg-white px-6 py-10 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <h1 className="text-2xl font-semibold text-[#0F172A]">Access denied</h1>
        <p className="mt-3 text-sm leading-6 text-[#64748B]">
          Only company administrators can manage company settings. Contact your
          admin if you need access.
        </p>
        <a
          href="/dashboard"
          className="mt-6 inline-flex h-10 items-center justify-center rounded-xl border border-[#E2E8F0] bg-white px-4 text-sm font-semibold text-[#0F172A] shadow-sm transition hover:bg-[#F8FAFC]"
        >
          Back to dashboard
        </a>
      </div>
    </div>
  );
}

function CompanySettingsLoading() {
  return (
    <div className="min-h-[40vh] bg-[#F6F8FB] px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl rounded-[18px] border border-[#E2E8F0] bg-white p-8 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <FormSectionLoadingSkeleton rows={10} />
      </div>
    </div>
  );
}

function CompanySettings(props) {
  const {
    addSnackbar,
    logout,
    updateTenantSuccess,
    user_type,
    permissions,
    roles,
    tenant,
  } = props;

  const canManage = canManageTenantSettings(user_type, permissions, roles);

  const {
    emptyModal,
    setEmptyModelOpen,
    loading,
    saving,
    uploadingLogo,
    formValues,
    logoPreview,
    logoFile,
    updateField,
    updateModule,
    updateLabel,
    resetChanges,
    handleLogoSelect,
    uploadLogo,
    saveSettings,
  } = CompanySettingsViewModel(
    addSnackbar,
    logout,
    updateTenantSuccess,
  );

  if (!canManage) {
    return <CompanySettingsForbidden />;
  }

  return (
    <div className="min-h-screen bg-[#F6F8FB] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <section className="rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <div className="border-b border-[#E2E8F0] px-6 py-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#64748B]">
                  QA Trace / Account
                </p>
                <h1 className="mt-2 text-[1.65rem] font-semibold tracking-tight text-[#0F172A] sm:text-3xl">
                  Company Settings
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-[#64748B]">
                  Manage your company profile, branding, modules, and labels.
                </p>
              </div>
              {tenant?.id && formValues?.name ? (
                <AuditActionLink
                  slug="tenant-settings"
                  id={tenant.id}
                  name={formValues.name}
                />
              ) : null}
            </div>
          </div>
        </section>

        {loading || !formValues ? (
          <CompanySettingsLoading />
        ) : (
          <CompanySettingsForm
            formValues={formValues}
            logoPreview={logoPreview}
            logoFile={logoFile}
            saving={saving}
            uploadingLogo={uploadingLogo}
            updateField={updateField}
            updateModule={updateModule}
            updateLabel={updateLabel}
            resetChanges={resetChanges}
            handleLogoSelect={handleLogoSelect}
            uploadLogo={uploadLogo}
            saveSettings={saveSettings}
          />
        )}

        <EmptyModal
          childComponent={emptyModal.childComponent}
          open={emptyModal.open}
          setOpen={setEmptyModelOpen}
        />
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    user_type: state.auth.user_type,
    permissions: state.auth.permissions,
    roles: state.auth.roles,
    tenant: state.auth.tenant,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    updateTenantSuccess: (tenant) =>
      dispatch(actions.updateTenantSuccess(tenant)),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(CompanySettings),
);
